import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, imgSrc } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";

const INPUT_CLASS =
  "w-full border border-white/10 bg-[#0d0d0d] px-4 py-3 text-sm font-light text-[#F5F5F5] placeholder:text-[#5a5a5a] transition-colors duration-300 focus:border-gold focus:outline-none rounded-none";

const EMPTY = { judul: "", deskripsi: "", foto: "", file: null, preview: null };

export default function AdminGaleri() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY }); };
  const openEdit = (g) => {
    setEditId(g.id);
    setForm({ judul: g.judul, deskripsi: g.deskripsi || "", foto: g.foto, file: null, preview: null });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file && !form.foto) {
      toast.error("Pilih foto terlebih dahulu.");
      return;
    }
    setSaving(true);
    try {
      let foto = form.foto;
      if (form.file) {
        const fd = new FormData();
        fd.append("file", form.file);
        const up = await api.post("/uploads", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        foto = up.data.url;
      }
      const payload = { judul: form.judul, deskripsi: form.deskripsi || null, foto };
      if (editId) {
        await api.put(`/gallery/${editId}`, payload);
        toast.success("Foto diperbarui.");
      } else {
        await api.post("/gallery", payload);
        toast.success("Foto ditambahkan ke galeri.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ylbh_admin_token");
        navigate("/admin/login");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Gagal menyimpan foto.");
      }
    } finally {
      setSaving(false);
    }
  };

  const hapus = async (g) => {
    if (!window.confirm(`Hapus foto "${g.judul}" dari galeri?`)) return;
    try {
      await api.delete(`/gallery/${g.id}`);
      toast.success("Foto dihapus.");
      load();
    } catch {
      toast.error("Gagal menghapus foto.");
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <AdminShell title="Kelola Galeri">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-4xl text-[#F5F5F5]">Galeri Kegiatan</h1>
        <button
          onClick={openCreate}
          data-testid="galeri-baru-button"
          className="flex items-center gap-2 bg-gold px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light"
        >
          <Plus size={15} /> Tambah Foto
        </button>
      </div>

      {form && (
        <form
          data-testid="galeri-form"
          onSubmit={submit}
          className="mt-8 space-y-5 border border-gold/30 bg-[#0A0A0A] p-6 lg:p-8"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl text-[#F5F5F5]">
              {editId ? "Ubah Foto" : "Foto Baru"}
            </h2>
            <button type="button" onClick={() => setForm(null)} aria-label="Tutup formulir" data-testid="galeri-form-close">
              <X size={20} className="text-[#737373] hover:text-white" />
            </button>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Judul Kegiatan *</label>
              <input data-testid="galeri-judul-input" required minLength={2} value={form.judul} onChange={set("judul")} className={INPUT_CLASS} placeholder="Penyuluhan hukum di Desa…" />
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">Deskripsi (opsional)</label>
              <input data-testid="galeri-deskripsi-input" value={form.deskripsi} onChange={set("deskripsi")} className={INPUT_CLASS} placeholder="Keterangan singkat kegiatan" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-2 block text-[11px] font-medium uppercase tracking-[0.3em] text-[#A3A3A3]">
                Foto — unggah dari komputer (JPG/PNG/WebP, maks 5 MB) *
              </label>
              <div className="flex flex-wrap items-center gap-5">
                <label
                  data-testid="galeri-upload-label"
                  className="flex cursor-pointer items-center gap-2 border border-dashed border-gold/50 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-ink"
                >
                  <ImagePlus size={15} /> Pilih Foto
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    data-testid="galeri-file-input"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setForm((prev) => ({ ...prev, file: f, preview: URL.createObjectURL(f) }));
                    }}
                  />
                </label>
                {(form.preview || form.foto) && (
                  <img
                    src={form.preview || imgSrc(form.foto)}
                    alt="Pratinjau foto galeri"
                    data-testid="galeri-preview-image"
                    className="h-20 w-28 border border-white/10 object-cover"
                  />
                )}
              </div>
            </div>
          </div>
          <button
            type="submit"
            data-testid="galeri-simpan-button"
            disabled={saving}
            className="bg-gold px-8 py-3.5 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all hover:bg-gold-light disabled:opacity-60"
          >
            {saving ? "Menyimpan…" : editId ? "Simpan Perubahan" : "Tambahkan"}
          </button>
        </form>
      )}

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items === null ? (
          <p className="col-span-full py-16 text-center font-serif text-xl italic text-[#737373]">Memuat…</p>
        ) : items.length === 0 ? (
          <p data-testid="galeri-admin-empty" className="col-span-full border border-white/10 bg-[#0d0d0d] py-16 text-center font-serif text-xl italic text-[#737373]">
            Belum ada foto di galeri. Klik "Tambah Foto" untuk memulai.
          </p>
        ) : (
          items.map((g) => (
            <div key={g.id} data-testid={`galeri-row-${g.id}`} className="group border border-white/10 bg-[#0d0d0d]">
              <img src={imgSrc(g.foto)} alt={g.judul} className="aspect-[4/3] w-full object-cover grayscale-[30%]" />
              <div className="p-4">
                <p className="font-serif text-base leading-snug text-[#F5F5F5]">{g.judul}</p>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => openEdit(g)}
                    data-testid={`galeri-edit-${g.id}`}
                    className="flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-[#A3A3A3] transition-colors hover:border-gold hover:text-gold"
                  >
                    <Pencil size={11} /> Ubah
                  </button>
                  <button
                    onClick={() => hapus(g)}
                    data-testid={`galeri-hapus-${g.id}`}
                    className="flex items-center gap-1.5 border border-red-500/30 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.2em] text-red-300/80 transition-colors hover:border-red-400 hover:text-red-300"
                  >
                    <Trash2 size={11} /> Hapus
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </AdminShell>
  );
}
